package com.macrofocus.docking.js

import com.macrofocus.common.command.Command
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.docking.DockingAnchor
import js.objects.unsafeJso
import org.mkui.component.CPComponent
import org.mkui.react.useProperty
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import web.cssom.*

/**
 * Tab shown in a docking bar, modelled after the Swing `DockingButton`: a compact toggle tab whose text runs
 * bottom-to-top in the west bar and top-to-bottom in the east bar, and whose text is bold when selected.
 */
class DockingButton(private val orientation: DockingAnchor, command: MutableProperty<Command?> = SimpleProperty(null), private val textProperty: MutableProperty<String?> = SimpleProperty("")) : CPComponent {
    val selectedProperty : MutableProperty<Boolean> = SimpleProperty(false)

    var text by textProperty
    var command by command
    var selected by selectedProperty
    var onRightClick: Command? = null

    override val nativeComponent: FC<*> = FC<Props> { props ->
        val (getText, _) = useProperty(textProperty)
        val (getSelected, _) = useProperty(selectedProperty)

        val sideBar = orientation == DockingAnchor.LEFT || orientation == DockingAnchor.RIGHT

        div {
            className = ClassName("DockingTab")
            asDynamic()["data-testid"] = "DockingButton:$getText"

            style = unsafeJso {
                display = if (sideBar) Display.block else Display.inlineBlock
                boxSizing = BoxSizing.borderBox
                margin = if (sideBar) Margin(1.px, 0.px) else Margin(0.px, 1.px)
                padding = if (sideBar) Padding(6.px, 3.px) else Padding(3.px, 6.px)
                border = Border(1.px, LineStyle.solid, rgb(156, 154, 149))
                // Rounded on the side facing outward, like the Swing tabs
                borderRadius = when (orientation) {
                    DockingAnchor.TOP -> "3px 3px 0 0"
                    DockingAnchor.BOTTOM -> "0 0 3px 3px"
                    DockingAnchor.LEFT -> "3px 0 0 3px"
                    else -> "0 3px 3px 0"
                }.unsafeCast<BorderRadius>()
                backgroundImage = (if (getSelected) {
                    "linear-gradient(rgb(228, 226, 221), rgb(211, 208, 201))"
                } else {
                    "linear-gradient(rgb(250, 250, 248), rgb(230, 228, 223))"
                }).unsafeCast<BackgroundImage>()
                fontSize = 12.px
                fontWeight = if (getSelected) FontWeight.bold else FontWeight.normal
                lineHeight = 16.px
                cursor = Cursor.pointer
                pointerEvents = PointerEvents.all
                userSelect = None.none
                whiteSpace = WhiteSpace.pre
            }

            onClick = {
                command.value?.execute()
            }

            onContextMenu = { event ->
                event.preventDefault()
                onRightClick?.execute()
            }

            div {
                className = ClassName("DockingButton")

                style = unsafeJso {
                    if (orientation == DockingAnchor.LEFT) {
                        writingMode = WritingMode.verticalLr
                        transform = rotate(180.deg)
                    } else if (orientation == DockingAnchor.RIGHT) {
                        writingMode = WritingMode.verticalLr
                    }
                }

                if (getText != null)
                    +getText
            }
        }
    }
}
